import { useState } from "react";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import type { McpClient, TokenEndpointAuthMethod } from "lib/hooks/api/auth/mcp/schemas";
import { useUpdateMcpClientAuthMethod } from "lib/hooks/api/auth/mcp/useUpdateMcpClientAuthMethod";
import { AuthMethodField } from "./AuthMethodField";

type Props = {
  credential: McpClient;
  currentMethod: TokenEndpointAuthMethod;
  onClose: () => void;
};

export function ChangeAuthMethodModal({ credential, currentMethod, onClose }: Props) {
  const [method, setMethod] = useState(currentMethod);
  const isUnchanged = method === currentMethod;

  const { mutate: updateAuthMethod, isLoading } = useUpdateMcpClientAuthMethod({
    onSuccess: () => {
      toast.success("Client authentication saved");
      onClose();
    },
    onError: (error) => toast.error(error.message ?? "Could not change the client authentication"),
  });

  return (
    <Modal open onOpenChange={onClose} maxWidth="520px" padding={24}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (isUnchanged) return;
          updateAuthMethod({ clientId: credential.client_id, tokenEndpointAuthMethod: method });
        }}
      >
        <Flex column gap={24}>
          <Flex column gap={8}>
            <Text h2>Change client authentication</Text>
            <Text description>
              The client ID and secret stay the same. Your tool has to send the secret the way
              chosen here, or signing in will fail.
            </Text>
          </Flex>

          <AuthMethodField value={method} onChange={setMethod} />

          <Flex justifyContent="flex-end" gap={8}>
            <Button type="button" buttonStyle="inverse" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || isUnchanged} loading={isLoading}>
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
